'use client'

import Link from 'next/link'

import { cn } from '@/lib/utils'
import {
  BookMeetingButton,
  useCalEmbed,
} from '@/components/book-meeting-button'
import { ChromaText } from '@/components/ui/chroma-text'
import { Eyebrow, HAIRLINE, SectionHead } from '@/components/page-kit'

/* ─── Page device: the invitation ──────────────────────────────────────────
   A scheduling page is an invite, so the booking panel is built as one — a
   record of fields (format, prep, who) with the action at its foot — and it
   rides along in a sticky rail while the left column explains the meeting.
   Nothing on this page hides behind a scroll: the CTA is on screen from the
   first pixel to the last.

   Every string below is the copy that was already on this page; the old
   "Why schedule a meeting with us?" trio is not deleted but promoted into the
   scope band under the hero, where it does work instead of repeating the
   "what to expect" list three sections later. */

/* The Cal namespace, link and embed setup now live in
   components/book-meeting-button.tsx so pages other than this one can offer the
   same booking action instead of linking to a placeholder page. */

/** Scope band — what the meeting is, as record fields rather than icon cards. */
const SCOPE = [
  {
    label: 'Expert consultation',
    value: 'Speak directly with product specialists who understand your needs',
  },
  {
    label: 'Live demos',
    value: 'See our solutions in action with personalized walkthroughs',
  },
  {
    label: 'Flexible scheduling',
    value: 'Choose times that work for you, across multiple time zones',
  },
]

/** The meeting itself, in order. */
const EXPECT = [
  {
    title: 'We listen first',
    desc: "You walk us through what you're dealing with: your current setup, where things slow down, and what you've already tried. No forms, no prep work needed.",
  },
  {
    title: 'We share what we think',
    desc: "Based on what you tell us, we'll give you our honest take: what makes sense, what doesn't, and what a realistic path forward looks like for your situation.",
  },
  {
    title: 'You leave with something useful',
    desc: "Even if we're not the right fit, you'll walk away with a clearer picture of your options and a few concrete things you can act on right away.",
  },
]

/** The invite's fields. */
const NEXT = [
  {
    label: 'Pick a time',
    value:
      'Choose a slot that works for your schedule. Video or in-person, your call.',
  },
  {
    label: 'We come prepared',
    value:
      "We'll review your context beforehand so we can ask the right questions from the start.",
  },
  {
    label: 'A clear direction',
    value:
      "You'll leave knowing exactly where things stand and what the right next move is.",
  },
]

/** The single booking action. Rendered twice (hero + invite panel). */
function BookButton({
  className,
  wrapperClassName,
  children = 'Schedule a Meeting',
}: {
  className?: string
  wrapperClassName?: string
  children?: React.ReactNode
}) {
  return (
    // initEmbed={false}: SchedulePage already calls useCalEmbed() once for the
    // whole page, and this button renders twice.
    <BookMeetingButton
      size="xl"
      initEmbed={false}
      className={cn(wrapperClassName, className)}
    >
      {children}
    </BookMeetingButton>
  )
}

export default function SchedulePage() {
  useCalEmbed()

  return (
    <main className="relative bg-background">
      {/* ─── Head ─────────────────────────────────────────────────────── */}
      <section
        className={cn(
          'relative overflow-hidden hero-adaptive-bg',
          'mt-[calc(var(--header-h)*-1)]'
        )}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-0"
          style={{
            backgroundImage: 'var(--circuit-bg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            WebkitMaskImage:
              'radial-gradient(ellipse 110% 70% at 32% 30%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
            maskImage:
              'radial-gradient(ellipse 110% 70% at 32% 30%, #000 16%, rgba(0,0,0,0.4) 58%, transparent 92%)',
          }}
        />

        <div className="container relative z-10 mx-auto">
          <div className="flex min-h-[62svh] flex-col justify-center pb-14 pt-[calc(var(--header-h)+3.5rem)] sm:pb-20">
            <nav aria-label="Breadcrumb">
              <ol className="flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
                <li>
                  <Link href="/" className="hover:text-foreground">
                    Home
                  </Link>
                </li>
                <li aria-hidden>/</li>
                <li className="text-foreground">Schedule a meeting</li>
              </ol>
            </nav>

            <div className="mt-6">
              <Eyebrow>Book a meeting</Eyebrow>
            </div>

            <h1 className="font-heading mt-6 max-w-4xl text-[32px]/[1.14] font-normal tracking-tight text-foreground sm:text-[44px]/[1.12] md:text-5xl/[1.1] lg:text-[60px]/[1.08]">
              <ChromaText>
                Schedule time with our{' '}
                <span className="text-emerald-600 dark:text-primary-active">
                  experts
                </span>
              </ChromaText>
            </h1>

            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg md:text-[19px]">
              Choose a date, time, and location that works for you. We&apos;ll
              help you explore how Euclideum can transform your institution.
            </p>

            <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
              <BookButton
                className="w-full"
                wrapperClassName="w-full sm:w-auto"
              />
              <p className="text-sm text-muted-foreground">
                Video or in-person. No forms, no prep work needed.
              </p>
            </div>
          </div>
        </div>

        {/* Scope band: rule full-bleed, cells in the container so the first
            one lines up under the headline. */}
        <div className={cn('relative z-10 border-y', HAIRLINE)}>
          <div className="container mx-auto">
            <dl className="grid gap-px bg-border/70 sm:grid-cols-3">
              {SCOPE.map((s) => (
                <div
                  key={s.label}
                  className="bg-background py-6 pr-4 sm:py-7 sm:pl-5 sm:pr-6 sm:first:pl-0"
                >
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
                    {s.label}
                  </dt>
                  <dd className="mt-2 max-w-xs text-sm leading-relaxed text-foreground">
                    {s.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* ─── The meeting, and the invite beside it ────────────────────── */}
      <section className="container mx-auto py-16 sm:py-20 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_21rem] lg:gap-16 xl:gap-24">
          <div>
            <SectionHead
              eyebrow="What to expect"
              // The line lands one word over the 2xl measure at lg, orphaning
              // "on"; break it where the sense breaks instead.
              title={
                <>
                  Tell us what you&apos;re
                  <br className="hidden lg:block" /> working on
                </>
              }
              deck="We'll listen, ask the right questions, and give you an honest view of how we can help, or point you in a better direction if we can't."
            />

            <div className={cn('mt-10 border-t sm:mt-12', HAIRLINE)}>
              {EXPECT.map((item) => (
                <div
                  key={item.title}
                  className={cn('border-b py-7 sm:py-9', HAIRLINE)}
                >
                  <h3 className="font-heading text-xl font-normal text-foreground sm:text-2xl">
                    {item.title}
                  </h3>
                  <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* The invite. Sticky on desktop so the action never scrolls away. */}
          <aside className="lg:sticky lg:top-[calc(var(--header-h)+2rem)] lg:self-start">
            <div
              className={cn(
                'rounded-2xl border p-6 sm:p-7',
                HAIRLINE,
                // Glass, not grey: translucent page colour keeps the blur alive.
                'bg-background/70 backdrop-blur-md'
              )}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-600 dark:text-primary-active">
                What happens next
              </p>

              <dl className={cn('mt-5 border-t', HAIRLINE)}>
                {NEXT.map((n) => (
                  <div
                    key={n.label}
                    className={cn('border-b py-4', HAIRLINE, 'last:border-b-0')}
                  >
                    <dt className="text-sm font-medium text-foreground">
                      {n.label}
                    </dt>
                    <dd className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                      {n.value}
                    </dd>
                  </div>
                ))}
              </dl>

              <BookButton className="w-full" wrapperClassName="mt-7 w-full" />

              <p className="mt-4 text-center text-xs text-muted-foreground">
                Prefer to write first?{' '}
                <Link
                  href="/support"
                  className="text-foreground underline underline-offset-4 hover:no-underline"
                >
                  Contact support
                </Link>
              </p>
            </div>
          </aside>
        </div>
      </section>
    </main>
  )
}
